import React, { Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { ProyectoProvider, useProyecto } from "./context/ProyectoContext";
import ProtectedRoute from "./components/ProtectedRoute";
import Loading from "./components/ui/Loading";
import PwaStatusBanner from "./components/PwaStatusBanner";
import ErrorBoundary from "./components/ErrorBoundary";

// Code Splitting - Lazy Loading de Páginas
const Login = lazy(() => import("./pages/Login"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Transacciones = lazy(() => import("./pages/Transacciones"));
const Reservas = lazy(() => import("./pages/Reservas"));
const Administracion = lazy(() => import("./pages/Administracion"));
const Liquidaciones = lazy(() => import("./pages/Liquidaciones"));
const InformeSocios = lazy(() => import("./pages/InformeSocios"));
const Cartera = lazy(() => import("./pages/Cartera"));
const Clientes = lazy(() => import("./pages/Clientes"));
const MiPortal = lazy(() => import("./pages/MiPortal"));
const Organizaciones = lazy(() => import("./pages/Organizaciones"));
const TrafficMetrics = lazy(() => import("./pages/TrafficMetrics"));
const Notificaciones = lazy(() => import("./pages/Notificaciones"));
const AdminNotificaciones = lazy(() => import("./pages/AdminNotificaciones"));
const MetaAdsCallback = lazy(() => import("./pages/MetaAdsCallback"));
const GoogleAdsCallback = lazy(() => import("./pages/GoogleAdsCallback"));
const Herramientas = lazy(() => import("./pages/Herramientas"));
const ReportViewer = lazy(() => import("./pages/ReportViewer"));

// Componente para redirigir según el rol
function HomeRedirect() {
  const { isVendedor, loading } = useProyecto();

  if (loading) return <Loading />;

  // Vendedores van a Mi Portal, otros van a Dashboard
  return <Navigate to={isVendedor ? "/mi-portal" : "/dashboard"} replace />;
}

// Componente para proteger rutas de vendedores (solo Mi Portal)
function RestrictedForVendedor({ children }) {
  const { isVendedor, loading } = useProyecto();

  if (loading) return <Loading />;

  // Si es vendedor, redirigir a Mi Portal
  if (isVendedor) {
    return <Navigate to="/mi-portal" replace />;
  }

  return children;
}

// Componente para proteger rutas solo para Admin/SuperAdmin
function AdminOnly({ children }) {
  const { isAdmin, isSuperAdmin, isVendedor, isLider, loading } = useProyecto();

  if (loading) return <Loading />;

  // Vendedores van a Mi Portal
  if (isVendedor) {
    return <Navigate to="/mi-portal" replace />;
  }

  // Líderes no tienen acceso a administración/socios
  if (isLider && !isAdmin && !isSuperAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

// Componente para proteger rutas solo para Platform Admin
function PlatformAdminOnly({ children }) {
  const { isPlatformAdmin, isVendedor, loading } = useProyecto();

  if (loading) return <Loading />;

  // Vendedores van a Mi Portal
  if (isVendedor) {
    return <Navigate to="/mi-portal" replace />;
  }

  // Solo Platform Admin puede acceder
  if (!isPlatformAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <ProyectoProvider>
          <BrowserRouter>
            <PwaStatusBanner />
            <Suspense fallback={<Loading />}>
              <ErrorBoundary>
                <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/oauth/meta/callback" element={<MetaAdsCallback />} />
              <Route path="/oauth/google-ads/callback" element={<GoogleAdsCallback />} />
              <Route path="/report/:id/:type" element={<ReportViewer />} />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <RestrictedForVendedor>
                      <Dashboard />
                    </RestrictedForVendedor>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/transacciones"
                element={
                  <ProtectedRoute>
                    <RestrictedForVendedor>
                      <Transacciones />
                    </RestrictedForVendedor>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cartera"
                element={
                  <ProtectedRoute>
                    <RestrictedForVendedor>
                      <Cartera />
                    </RestrictedForVendedor>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reservas"
                element={
                  <ProtectedRoute>
                    <Reservas />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/administracion"
                element={
                  <ProtectedRoute>
                    <AdminOnly>
                      <Administracion />
                    </AdminOnly>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/liquidaciones"
                element={
                  <ProtectedRoute>
                    <RestrictedForVendedor>
                      <Liquidaciones />
                    </RestrictedForVendedor>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/socios"
                element={
                  <ProtectedRoute>
                    <AdminOnly>
                      <InformeSocios />
                    </AdminOnly>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/clientes"
                element={
                  <ProtectedRoute>
                    <RestrictedForVendedor>
                      <Clientes />
                    </RestrictedForVendedor>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/mi-portal"
                element={
                  <ProtectedRoute>
                    <MiPortal />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/organizaciones"
                element={
                  <ProtectedRoute>
                    <PlatformAdminOnly>
                      <Organizaciones />
                    </PlatformAdminOnly>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/traffic"
                element={
                  <ProtectedRoute>
                    <AdminOnly>
                      <TrafficMetrics />
                    </AdminOnly>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/herramientas"
                element={
                  <ProtectedRoute>
                    <AdminOnly>
                      <Herramientas />
                    </AdminOnly>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/notificaciones"
                element={
                  <ProtectedRoute>
                    <Notificaciones />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/notificaciones"
                element={
                  <ProtectedRoute>
                    <PlatformAdminOnly>
                      <AdminNotificaciones />
                    </PlatformAdminOnly>
                  </ProtectedRoute>
                }
              />
              <Route path="/" element={<ProtectedRoute><HomeRedirect /></ProtectedRoute>} />
              <Route path="*" element={<ProtectedRoute><HomeRedirect /></ProtectedRoute>} />
            </Routes>
          </ErrorBoundary>
        </Suspense>
      </BrowserRouter>
    </ProyectoProvider>
  </AuthProvider>
</ErrorBoundary>
  );
}

export default App;
